
/**
 * Inline SVG icons used by the recipient page, copied from the v3 design markup so the strokes
 * and proportions match the design exactly. All inherit `currentColor`.
 */

interface IconProps {
	size?: number;
	strokeWidth?: number;
}

/** Padlock (design lock-art / protected banner icon). */
export function LockIcon({size = 30, strokeWidth = 1.8}: IconProps) {
	return (
		<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
			 strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
			<rect x="5" y="11" width="14" height="9" rx="2"/>
			<path d="M8 11V8a4 4 0 0 1 8 0v3"/>
		</svg>
	);
}

/** Cloud (design "Stored on Transferify" banner icon). */
export function CloudIcon({size = 16, strokeWidth = 2}: IconProps) {
	return (
		<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
			 strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
			<path d="M17.5 19a4.5 4.5 0 0 0 .5-8.97A6 6 0 0 0 6.34 9.5 4 4 0 0 0 7 17.5"/>
		</svg>
	);
}

/**
 * Wrapped gift (the referral offer's trigger, kitecorp/transferify-ui#1434).
 *
 * A gift box rather than a coin, a percent sign or a ticket: the offer's own copy is written as
 * something the recipient RECEIVES because somebody sent it to them — an invitation from a named
 * sender whose code is already applied — which is the one thing a wrapped box says without a label.
 * It is the only icon on this card that is not a noun from the transfer itself, so it also cannot be
 * mistaken for a file, a lock or a download.
 *
 * The mark has now outlived two rewards it was chosen against, and neither is quoted here: a comment
 * repeating superseded copy is how that wording kept circulating. What it marks today is api#1228's
 * offer — an intro-priced first period on Plus or Premium, with a card — which a gift fits better than
 * it fitted either of the others.
 *
 * Drawn at the same 24-unit grid and stroke conventions as its neighbours above, so it sits in the
 * footer at the same optical weight as the rest of the card's furniture.
 */
/** A ticket with a percent sign: the coupon family's glyph on its pull (owner 2026-09-09). */
export function CouponIcon({size = 16, strokeWidth = 1.8}: IconProps) {
	return (
		<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
			 strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
			<path d="M4 5h16a1 1 0 0 1 1 1v3.5a2.5 2.5 0 0 0 0 5V18a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-3.5a2.5 2.5 0 0 0 0-5V6a1 1 0 0 1 1-1z"/>
			<path d="m9 15 6-6"/>
			<circle cx="9.5" cy="9.5" r="1"/>
			<circle cx="14.5" cy="14.5" r="1"/>
		</svg>
	);
}

export function GiftIcon({size = 16, strokeWidth = 1.8}: IconProps) {
	return (
		<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
			 strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
			<path d="M20 12v9H4v-9"/>
			<rect x="2" y="7" width="20" height="5" rx="1"/>
			<path d="M12 21V7"/>
			<path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/>
			<path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>
		</svg>
	);
}

/** Downward arrow (design per-file + download-all icon). */
export function DownArrowIcon({size = 15, strokeWidth = 2}: IconProps) {
	return (
		<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
			 strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
			<path d="M12 4v13"/>
			<path d="M6 11l6 6 6-6"/>
			<path d="M5 21h14"/>
		</svg>
	);
}
